import {useCallback, useEffect, useState} from "react";
import {Clock, RotateCcw, User} from "lucide-react";
import {getAdminReturn, listAdminReturns, updateAdminReturnStatus, deleteAdminReturn, type AdminReturnDetail, type AdminReturnSummary} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {adminDateRangeToApi, EMPTY_ADMIN_DATE_RANGE, type AdminDateRangeValue} from "@/lib/adminDateRange";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAlert, AdminBadge, AdminDateRangePicker, AdminEditModal, AdminFilterBar, AdminFilterField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminRowTrailing} from "./ui";

const STATUS_OPTS = [
  {v: 0, l: "All"},
  {v: 1, l: "PROCESSING"},
  {v: 2, l: "ACCEPTED"},
  {v: 3, l: "COMPLETED"},
  {v: 4, l: "REJECTED"}
];

const R_OPS = [
  {op: "Rstatus1", l: "Processing", s: 1},
  {op: "Rstatus2", l: "Accepted", s: 2},
  {op: "Rstatus4", l: "Rejected", s: 4},
  {op: "Rstatus3", l: "Completed", s: 3}
];

export function ReturnsView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const [items, setItems] = useState<AdminReturnSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<AdminDateRangeValue>(EMPTY_ADMIN_DATE_RANGE);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminReturnDetail | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    const dates = adminDateRangeToApi(dateRange);
    listAdminReturns({
      page,
      pageSize: 20,
      status: statusFilter || undefined,
      search: search || undefined,
      dateFrom: dates.dateFrom,
      dateTo: dates.dateTo
    }).then((r) => {
      setItems(r.items);
      setTotalPages(r.totalPages);
    }).catch((e: Error) => setErr(e.message));
  }, [page, statusFilter, search, dateRange]);

  useEffect(() => { load(); }, [page, statusFilter, dateRange]);

  function resetFilters() {
    setStatusFilter(0);
    setSearch("");
    setDateRange(EMPTY_ADMIN_DATE_RANGE);
    setPage(1);
  }

  async function openReturn(id: number) {
    setOpenId(id);
    setModalOpen(true);
    setDetail(await getAdminReturn(id));
  }

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) { setOpenId(null); setDetail(null); }
  }

  async function refreshDetail(id: number) {
    setDetail(await getAdminReturn(id));
  }

  function applyStatus(id: number, op: string) {
    if(op === "Rstatus4") {
      const motive = window.prompt("Rejection reason:");
      if(motive === null) return;
      if(!motive.trim()) { adminToast.error("Reason is required."); return; }
      updateAdminReturnStatus(id, op, {respingere: motive.trim()}).then(() => {
        load();
        refreshDetail(id);
        adminToast.success("Status updated.");
      });
      return;
    }
    confirm("Confirm status change?", async () => {
      await updateAdminReturnStatus(id, op);
      load();
      await refreshDetail(id);
      adminToast.success("Status updated.");
    });
  }

  function removeReturn(id: number) {
    confirm("Delete this return request?", async () => {
      await deleteAdminReturn(id);
      closeModal(false);
      load();
      adminToast.success("Return deleted.");
    }, "Delete", "Delete");
  }

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;

  return (
    <AdminPageLayout
      title="Returns"
      description="Order return requests"
      breadcrumbs={[
        {label: "Dashboard", href: "/vadmin"},
        {label: "Returns"}
      ]}
    >
      {confirmDialog}
      <AdminFilterBar
        onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
        onReset={resetFilters}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Name, email, phone..."
        itemCount={items.length}
      >
        <AdminFilterField label="Period">
          <AdminDateRangePicker value={dateRange} onChange={setDateRange} />
        </AdminFilterField>
        <AdminFilterField label="Status">
          <Select value={String(statusFilter)} onValueChange={(v) => setStatusFilter(Number(v))}>
            <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </AdminFilterField>
      </AdminFilterBar>
      <AdminModernTable
        items={items}
        getId={(r) => r.id}
        getTitle={(r) => r.nume}
        getSubtitle={(r) => r.email}
        getMeta={(r) => (
          <>
            <AdminBadge status={r.status}>{r.statusLabel}</AdminBadge>
            <AdminBadge variant="secondary"><Clock className="h-3 w-3" /> {r.dateLabel}</AdminBadge>
          </>
        )}
        getSearchText={(r) => adminSearchText(
          r.id, r.nume, r.email, r.telefon, r.statusLabel, r.dateLabel,
          detail?.id === r.id ? detail.adresa : undefined,
          detail?.id === r.id ? detail.motiv : undefined,
          detail?.id === r.id ? detail.produse : undefined,
          detail?.id === r.id ? detail.telefon : undefined,
          detail?.id === r.id ? detail.optiuneLabel : undefined,
          detail?.id === r.id ? detail.respingere : undefined
        )}
        selectedId={openId}
        onRowClick={(r) => openReturn(r.id)}
        emptyTitle="No returns"
        emptyDescription="Adjust filters or check back later."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
        getRowActions={(r) => (
          <AdminRowTrailing onDelete={() => removeReturn(r.id)} deleteLabel="Delete return" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={detail ? `Return #${detail.id}` : "Return"}
        icon={RotateCcw}
        size="lg"
      >
        {detail ? (
          <>
            <AdminFormActions>
              {R_OPS.map((o) => (
                <Button key={o.op} type="button" variant={detail.status === o.s ? "default" : "outline"} size="sm" onClick={() => applyStatus(detail.id, o.op)}>{o.l}</Button>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => removeReturn(detail.id)}>Delete</Button>
            </AdminFormActions>
            <h3 className="admin-detail-panel__heading"><User className="inline h-4 w-4" /> {detail.nume} — {detail.email}</h3>
            <p><b>Phone:</b> {detail.telefon} | <b>Address:</b> {detail.adresa}</p>
            <p><b>Option:</b> {detail.optiuneLabel}</p>
            <p><b>Products:</b> {detail.produse}</p>
            <p><b>Reason:</b> {detail.motiv}</p>
            <p><b>Order date:</b> {detail.dataComanda} | <b>Delivery date:</b> {detail.dataLivrare}</p>
            {detail.files.length ? (
              <div className="admin-detail-panel__section">
                <b>Images:</b>
                {detail.files.map((f) => <div key={f.url}><a href={f.url} target="_blank" rel="noreferrer">{f.name}</a></div>)}
              </div>
            ) : null}
            {detail.respingere ? <p><b>Rejection reason:</b> {detail.respingere}</p> : null}
          </>
        ) : null}
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
